import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { CurrencyEur, Calendar, Buildings } from "@phosphor-icons/react";
import { getApiErrorMessage } from "@/lib/apiError";
import { PageHeader } from "@/components/PageHeader";
import { LoadingSpinner } from "@/components/LoadingSpinner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('pt-PT', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

export default function Comissoes() {
  const [vendasData, setVendasData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const response = await axios.get(`${API}/sales/vendas`);
        setVendasData(response.data);
      } catch (error) {
        console.error("Error loading commissions:", error);
        toast.error("Erro ao carregar comissões: " + getApiErrorMessage(error));
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  const monthlyCommissions = vendasData?.monthly_sales || [];
  const clientCommissions = (vendasData?.sales_by_customer || [])
    .filter(c => (c.commission || 0) > 0)
    .sort((a, b) => (b.commission || 0) - (a.commission || 0));

  const totalCommission = vendasData?.totals?.total_commission || 0;

  return (
    <div className="space-y-6 animate-fade-in" data-testid="comissoes-page">
      <PageHeader
        icon={CurrencyEur}
        title="Comissões"
        subtitle="Resumo de comissões por mês e por cliente"
      />

      {loading && (
        <div className="gresart-card text-center py-8">
          <LoadingSpinner size={40} className="mx-auto mb-2" />
          <p className="text-stone-500">Carregando dados...</p>
        </div>
      )}

      {!loading && !vendasData?.monthly_sales?.length && !vendasData?.sales_by_customer?.length && (
        <div className="gresart-card text-center py-8" data-testid="comissoes-empty-state">
          <CurrencyEur size={40} className="mx-auto text-[#009392] mb-2" />
          <p className="text-stone-600 font-medium">Ainda não há dados de vendas carregados</p>
          <p className="text-stone-400 text-sm mt-1">Carregue os ficheiros GPP em Configurações → Dados de Vendas (GPP)</p>
        </div>
      )}

      {!loading && (vendasData?.monthly_sales?.length > 0 || vendasData?.sales_by_customer?.length > 0) && (
        <>
          <div className="gresart-card bg-gradient-to-br from-amber-500/10 to-transparent border-amber-500/30 text-center py-4">
            <p className="text-sm text-stone-500 font-medium">Total de Comissões</p>
            <p className="text-3xl font-bold text-amber-600" data-testid="comissoes-total">{formatCurrency(totalCommission)}</p>
            <p className="text-xs text-stone-400 mt-1">{vendasData?.totals?.period || 'YTD'}</p>
          </div>

          {/* Resumo por Mês */}
          {monthlyCommissions.length > 0 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Calendar size={20} className="text-[#009392]" />
                Comissões por Mês
              </h3>
              <div className="overflow-x-auto">
                <table className="w-full text-xs sm:text-sm" data-testid="comissoes-by-month-table">
                  <thead>
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-3 font-medium text-stone-600">Mês</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-600">Vendas</th>
                      <th className="text-right py-2 px-3 font-medium text-amber-600">Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {monthlyCommissions.map((item, idx) => (
                      <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`comissoes-month-row-${idx}`}>
                        <td className="py-2 px-3 font-medium text-stone-700">{item.month}</td>
                        <td className="py-2 px-3 text-right text-stone-600">{formatCurrency(item.sales)}</td>
                        <td className="py-2 px-3 text-right text-amber-600 font-medium">{formatCurrency(item.commission)}</td>
                      </tr>
                    ))}
                    <tr className="bg-stone-50 font-bold">
                      <td className="py-2 px-3 text-stone-800">TOTAL</td>
                      <td className="py-2 px-3 text-right text-[#009392]">
                        {formatCurrency(monthlyCommissions.reduce((sum, m) => sum + (m.sales || 0), 0))}
                      </td>
                      <td className="py-2 px-3 text-right text-amber-600">
                        {formatCurrency(monthlyCommissions.reduce((sum, m) => sum + (m.commission || 0), 0))}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* Total por Cliente */}
          {clientCommissions.length > 0 && (
            <div className="gresart-card">
              <h3 className="text-lg font-semibold text-stone-800 mb-4 flex items-center gap-2">
                <Buildings size={20} className="text-[#009392]" />
                Comissões por Cliente ({clientCommissions.length})
              </h3>
              <div className="max-h-[32rem] overflow-y-auto border border-stone-200 rounded-lg">
                <table className="w-full text-xs sm:text-sm" data-testid="comissoes-by-client-table">
                  <thead className="sticky top-0 bg-stone-100">
                    <tr className="border-b border-stone-200">
                      <th className="text-left py-2 px-3 font-medium text-stone-600">#</th>
                      <th className="text-left py-2 px-3 font-medium text-stone-600">Cliente</th>
                      <th className="text-right py-2 px-3 font-medium text-stone-600">Vendas</th>
                      <th className="text-right py-2 px-3 font-medium text-amber-600">Comissão</th>
                    </tr>
                  </thead>
                  <tbody>
                    {clientCommissions.map((item, idx) => (
                      <tr key={idx} className="border-b border-stone-100 hover:bg-stone-50" data-testid={`comissoes-client-row-${idx}`}>
                        <td className="py-2 px-3 text-stone-400">{idx + 1}</td>
                        <td className="py-2 px-3 font-medium text-stone-700 truncate max-w-[40vw] sm:max-w-none" title={item.customer_full || item.customer}>
                          {item.customer}
                        </td>
                        <td className="py-2 px-3 text-right text-stone-600 whitespace-nowrap">{formatCurrency(item.sales)}</td>
                        <td className="py-2 px-3 text-right text-amber-600 font-medium whitespace-nowrap">{formatCurrency(item.commission)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
